/*
 * @Description: 个人轨迹回放 — 右侧「成员轨迹」面板
 *   成员下拉 + 状态行(A4) + 4 数据格(B4 里程/时长 + B3 停留/拍照) + 节点时间轴列表(B3)。
 */
import React from 'react'
import { Select } from 'antd'
import { DoubleRightOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  MemberDetail,
  TimelineData,
  TimelineEvent,
  TrackStatsData
} from '../../../types/cockpit.types'
import './index.scss'

const { Option } = Select

/** 收起态「轨迹」标签图标（设计稿 Frame3，22×22 定位图标） */
const TrackTabIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path
      d="M16.8076 19.569C17.9626 19.569 18.9306 18.678 18.9306 17.556C18.9306 16.434 17.9626 15.543 16.8076 15.543H5.18062C4.74062 15.543 4.41062 15.235 4.41062 14.883C4.41062 14.531 4.74062 14.223 5.18062 14.223H7.58962C7.96362 14.223 8.27162 13.915 8.27162 13.541C8.26062 13.156 7.96362 12.859 7.58962 12.859H5.18062C4.02562 12.859 3.05762 13.75 3.05762 14.872C3.05762 15.994 4.02562 16.885 5.18062 16.885H16.8076C17.2476 16.885 17.5776 17.193 17.5776 17.545C17.5776 17.897 17.2476 18.205 16.8076 18.205H7.10562C6.74262 18.194 6.43462 18.502 6.42362 18.887C6.42362 19.261 6.73162 19.569 7.10562 19.569H16.8076Z"
      fill="#0070F4"
    />
    <path
      d="M11.6161 2.40898C9.16313 2.39798 7.17213 4.38898 7.16113 6.85298C7.16113 8.67898 9.62513 12.089 10.8901 13.717C11.2421 14.113 11.8581 14.157 12.2541 13.805L12.3421 13.717C13.6071 12.089 16.0711 8.68998 16.0711 6.85298C16.0711 4.38898 14.0801 2.40898 11.6161 2.40898ZM11.6381 8.85498C11.1101 8.85498 10.6041 8.64598 10.2301 8.27198C9.85613 7.89798 9.64713 7.39198 9.64713 6.86398C9.64713 6.33598 9.85613 5.82998 10.2301 5.45598C10.6041 5.08198 11.1101 4.87298 11.6381 4.87298C12.7271 4.89498 13.5961 5.77498 13.5961 6.87498C13.6071 7.95298 12.7271 8.84398 11.6381 8.85498Z"
      fill="#0070F4"
    />
  </svg>
)

interface TrackSidePanelProps {
  /** 成员下拉选项（团队成员 A2） */
  memberOptions: Array<{ userId: string; name: string }>
  /** 当前成员 userId */
  selectedUserId: string
  /** A4 成员详情（状态行） */
  detail: MemberDetail | null
  /** B4 轨迹统计（4 数据格里程/时长/拍照） */
  stats: TrackStatsData | null
  /** B3 轨迹（停留点数 + 节点列表） */
  timeline: TimelineData | null
  /** 切换成员 */
  onChangeMember: (userId: string) => void
  /** 是否收起（收起后只剩右侧「轨迹」标签） */
  collapsed: boolean
  /** 切换收起/展开 */
  onToggleCollapse: () => void
  /** 点击节点（联动地图） */
  onSelectNode: (nodeIndex: number) => void
}

/** 信号格数 → 文案 */
function signalText(signal?: number): string {
  if (signal == null) return '--'
  if (signal >= 4) return '良好'
  if (signal >= 2) return '一般'
  return '较弱'
}

/** 电量图标（填充宽度按百分比；≤20% 红，否则绿） */
const BatteryIcon: React.FC<{ level: number }> = ({ level }) => {
  const lvl = Math.max(0, Math.min(100, level || 0))
  return (
    <svg width="20" height="11" viewBox="0 0 20 11" fill="none">
      <rect x="0.5" y="0.5" width="15" height="10" rx="2.5" stroke="#bfbfbf" />
      <rect
        x="2.5"
        y="2.5"
        width={(10 * lvl) / 100}
        height="6"
        rx="1"
        fill={lvl <= 20 ? '#ff4d4f' : '#52c41a'}
      />
      <rect x="16.5" y="3.5" width="2.5" height="4" rx="1" fill="#bfbfbf" />
    </svg>
  )
}

/** 信号图标（4 格，按 signal 高亮） */
const SignalIcon: React.FC<{ level: number }> = ({ level }) => (
  <svg width="15" height="9" viewBox="0 0 15 9">
    {[3, 5, 7, 9].map((h, i) => (
      <rect
        key={i}
        x={i * 4}
        y={9 - h}
        width="3"
        height={h}
        rx="0.5"
        fill={(level || 0) >= i + 1 ? '#595959' : '#d9d9d9'}
      />
    ))}
  </svg>
)

/** 步行图标（移动段前缀） */
const WalkIcon: React.FC = () => (
  <svg width="12" height="14" viewBox="0 0 24 24" fill="#bfbfbf">
    <path d="M13.5 5.5a2 2 0 100-4 2 2 0 000 4zM9.8 8.9 7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.5-.7-1.1-1-1.8-1-.3 0-.5 0-.8.1L6 8.3V13h2V9.6l1.8-.7z" />
  </svg>
)

/** 秒 → "X小时Y分钟" / "Y分钟" */
function formatDuration(sec: number): string {
  const totalMin = Math.round(sec / 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0) return `${h}小时${m}分钟`
  return `${m}分钟`
}

/** 米 → "X.X千米" / "X米" */
function formatDistance(meters: number): string {
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)}千米`
  return `${Math.round(meters)}米`
}

/** 移动段文案：移动X分钟, 距离Y */
function moveText(e: Extract<TimelineEvent, { type: 'move' }>): string {
  const dur = e.durationSec ? formatDuration(e.durationSec) : ''
  const dist = e.distanceM ? formatDistance(e.distanceM) : ''
  return [dur && `移动${dur}`, dist && `距离${dist}`].filter(Boolean).join('，')
}

const TrackSidePanel: React.FC<TrackSidePanelProps> = ({
  memberOptions,
  selectedUserId,
  detail,
  stats,
  timeline,
  onChangeMember,
  collapsed,
  onToggleCollapse,
  onSelectNode
}) => {
  // 收起：右侧只留一个「轨迹」标签，点击展开
  if (collapsed) {
    return (
      <div className="replay-side-rail">
        <div className="replay-side-tab" onClick={onToggleCollapse}>
          <span className="replay-side-tab__icon">
            <TrackTabIcon />
          </span>
          <span className="replay-side-tab__label">轨迹</span>
        </div>
      </div>
    )
  }

  // 4 数据格
  const activeHours = stats ? (stats.totalDurationSec / 3600).toFixed(1) : '--'
  const totalKm = stats ? (stats.totalDistanceMeters / 1000).toFixed(1) : '--'
  const stayCount = timeline ? timeline.summary.stayPointCount : 0
  const photoCount = stats
    ? stats.photoCount
    : timeline
      ? timeline.summary.photoCount
      : 0

  // 节点列表行（节点 + 移动/中断）
  const events = timeline?.events ?? []
  let nodeCounter = -1

  return (
    <div className="replay-side">
      <div className="replay-side__header">
        <span className="replay-side__title">成员轨迹</span>
        {/* 折叠图标：点击直接收起为右侧「轨迹」标签 */}
        <DoubleRightOutlined
          className="replay-side__close"
          onClick={onToggleCollapse}
        />
      </div>

      <div className="replay-side__member">
        <Select
          value={selectedUserId}
          style={{ width: '100%' }}
          onChange={onChangeMember}
          showSearch
          optionFilterProp="children"
        >
          {memberOptions.map((m) => (
            <Option key={m.userId} value={m.userId}>
              {m.name}
            </Option>
          ))}
        </Select>
      </div>

      {/* 状态行：状态点·在线/离线 | 电量 | 信号（竖线分隔） */}
      <div
        className={`replay-side__status ${
          detail && detail.online ? 'is-online' : 'is-offline'
        }`}
      >
        <span className="replay-side__seg replay-side__state">
          <span
            className={`replay-side__dot ${
              detail && detail.online ? 'is-online' : 'is-offline'
            }`}
          />
          {detail ? (detail.online ? '在线' : '离线') : '--'}
        </span>
        {detail && (
          <>
            <span className="replay-side__sep" />
            <span className="replay-side__seg">
              <span className="replay-side__seg-icon">
                <BatteryIcon level={detail.battery} />
              </span>
              {detail.battery}%
            </span>
            <span className="replay-side__sep" />
            <span className="replay-side__seg">
              <span className="replay-side__seg-icon">
                <SignalIcon level={detail.signal} />
              </span>
              {signalText(detail.signal)}
            </span>
          </>
        )}
      </div>

      {/* 4 数据格：上行数值+单位、下行标签 */}
      <div className="replay-side__cells">
        <div className="replay-side__cell">
          <div className="replay-side__cell-num">
            <span className="replay-side__cell-val">{activeHours}</span>
            <span className="replay-side__cell-unit">小时</span>
          </div>
          <div className="replay-side__cell-label">已活动</div>
        </div>
        <div className="replay-side__cell">
          <div className="replay-side__cell-num">
            <span className="replay-side__cell-val">{totalKm}</span>
            <span className="replay-side__cell-unit">km</span>
          </div>
          <div className="replay-side__cell-label">总里程</div>
        </div>
        <div className="replay-side__cell">
          <div className="replay-side__cell-num">
            <span className="replay-side__cell-val">{stayCount}</span>
            <span className="replay-side__cell-unit">个</span>
          </div>
          <div className="replay-side__cell-label">停留点</div>
        </div>
        <div className="replay-side__cell">
          <div className="replay-side__cell-num">
            <span className="replay-side__cell-val">{photoCount}</span>
            <span className="replay-side__cell-unit">张</span>
          </div>
          <div className="replay-side__cell-label">拍照</div>
        </div>
      </div>

      {/* 节点时间轴列表 */}
      <div className="replay-side__list">
        {events.map((e, i) => {
          if (e.type === 'move') {
            if (e.lineType === 'dashed') {
              const start = dayjs(e.startTime).format('HH:mm')
              const end = dayjs(e.endTime).format('HH:mm')
              const gap = e.gapDurationSec
                ? formatDuration(e.gapDurationSec)
                : ''
              return (
                <div
                  className="replay-row replay-row--node replay-row--gapnode"
                  key={`gap-${i}`}
                >
                  <span className="replay-row__icon is-gap">!</span>
                  <div className="replay-row__body">
                    <div className="replay-row__line1">
                      <span className="replay-row__time">{start}</span>
                    </div>
                    <div className="replay-row__gapbox">
                      <div className="replay-row__gaptext">
                        {gap}未能获取轨迹
                      </div>
                      <div className="replay-row__gaptime">
                        {start}-{end}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
            return (
              <div className="replay-row replay-row--move" key={`move-${i}`}>
                <span className="replay-row__move-icon">
                  <WalkIcon />
                </span>
                {moveText(e)}
              </div>
            )
          }

          // 节点行
          nodeCounter += 1
          const nodeIndex = nodeCounter
          const dotKind =
            e.type === 'start'
              ? 'is-start'
              : e.type === 'stay'
                ? 'is-stay'
                : e.type === 'photo'
                  ? 'is-photo'
                  : 'is-current'
          const dotInner =
            e.type === 'start'
              ? '起'
              : e.type === 'stay'
                ? '停'
                : e.type === 'photo'
                  ? '📷'
                  : // 结束点：当天=现，非当天=终（按该节点时间判断，与回放地图一致）
                    dayjs(e.time).isSame(dayjs(), 'day')
                    ? '现'
                    : '终'
          // 用 e.type 直接收窄，TS 才能识别 photos / photoCount
          const photos =
            e.type === 'stay' || e.type === 'photo' ? e.photos : undefined
          const cnt =
            e.type === 'stay' ? e.photoCount : photos ? photos.length : 0

          return (
            <div
              className="replay-row replay-row--node"
              key={`node-${i}`}
              onClick={() => onSelectNode(nodeIndex)}
            >
              <span className={`replay-row__icon ${dotKind}`}>{dotInner}</span>
              <div className="replay-row__body">
                <div className="replay-row__line1">
                  <span className="replay-row__time">
                    {dayjs(e.time).format('HH:mm')}
                  </span>
                  <span className="replay-row__addr">
                    {e.address || '位置'}
                  </span>
                </div>
                {e.type === 'stay' && (
                  <div className="replay-row__sub">
                    {/* 时长缺失/为 0：还在停留中，不渲染 NaN分钟 / Invalid date */}
                    {e.stayDurationSec
                      ? `停留${Math.round(
                          e.stayDurationSec / 60
                        )}分钟 (${dayjs(e.time).format('HH:mm')}-${dayjs(
                          e.time + e.stayDurationSec * 1000
                        ).format('HH:mm')})`
                      : '停留中'}
                  </div>
                )}
                {photos && photos.length > 0 && (
                  <div className="replay-row__photos">
                    {photos.slice(0, 3).map((p, idx) => (
                      <span className="replay-row__thumb" key={idx}>
                        <img src={p.smallUrl} alt="" />
                      </span>
                    ))}
                    {!!cnt && cnt > 3 && (
                      <span className="replay-row__more">共 {cnt} 张</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TrackSidePanel
